import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { LinkSenderRole } from '@/domain/types';

export type ModalKey =
  | 'persona-switcher'
  | 'studio-switcher'
  | 'self-book-generator'
  | 'cancellation'
  | 'booking-modal'
  | 'rebooking-options'
  | 'deposit-link-modal'
  | 'rapport-note';

export interface NotificationToast {
  id: string;
  intensity: 'info' | 'high' | 'critical';
  title: string;
  body?: string;
  ttlMs: number;
  createdAt: string;
  studioContext?: string;
  customerId?: string;
}

export interface IncomingCallNotification {
  id: string;
  customerId: string;
  studioId: string;
  startedAt: string;
  status: 'ringing' | 'answered' | 'declined' | 'overflow';
  /**
   * B7 (75nh D08-26): the Sweeper this client's callback routes to (they called
   * in the last 7 days). Set, it rings that person only; unset, it rings anyone.
   */
  routedToPid?: string;
  routedToName?: string;
  /**
   * L5 (D05-05): why it is routed — a Sweeper's callback (B7), or the studio's
   * own agent (the call is to a studio the signed-in agent doesn't cover).
   */
  routeKind?: 'callback' | 'studio';
}

/**
 * What the operator is working on right now, when it changes WHO a deposit link
 * is from (W5b). Set by the surface that opens the work (the Sweeper's gap-fill
 * window, a "Pick a new date" resend) and cleared when it closes. Scoped to one
 * customer: generateDepositLink only reads it for a link to that customer, so a
 * stale context can never re-label an unrelated send.
 * - resend:   a resend that fell through to "Pick a new date" — the new link is
 *             the RESENDER's (Q3), e.g. 'sweeper' from Missed deposits.
 * - gap-fill: the Sweeper's gap window (QueueWorkModal with a gap). ANY link sent
 *             from it — the gap-fill panel or the generic composer/deposit path —
 *             is a Sweeper gap-fill link (sentByRole 'sweeper', source 'gap-fill',
 *             firm 24h hold), so it lands on Missed deposits (D19).
 */
export type WorkContext =
  | { kind: 'resend'; customerId: string; sentByRole?: LinkSenderRole }
  | { kind: 'gap-fill'; customerId: string; artistId: string; startIso: string; endIso: string };

export interface UIState {
  controlPanelOpen: boolean;
  activityFeedOpen: boolean;
  railCollapsed: boolean;
  modals: Record<ModalKey, boolean>;
  modalContext: Record<string, unknown>;
  notifications: NotificationToast[];
  incomingCalls: IncomingCallNotification[];
  muted: boolean;
  narrationOn: boolean;
  workContext: WorkContext | null;
  setWorkContext: (ctx: WorkContext | null) => void;
  toggleControlPanel: () => void;
  toggleActivityFeed: () => void;
  toggleRail: () => void;
  setModal: (key: ModalKey, open: boolean, context?: Record<string, unknown>) => void;
  pushNotification: (n: Omit<NotificationToast, 'id' | 'createdAt'>) => void;
  dismissNotification: (id: string) => void;
  addIncomingCall: (call: Omit<IncomingCallNotification, 'status'>) => void;
  setIncomingCallStatus: (id: string, status: IncomingCallNotification['status']) => void;
  clearIncomingCalls: () => void;
  setMuted: (muted: boolean) => void;
  setNarration: (on: boolean) => void;
}

let _toastSeed = 0;
const nextId = (prefix: string) => `${prefix}-${Date.now()}-${++_toastSeed}`;

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
  controlPanelOpen: false,
  // Off by default. It is a permanent right-hand column, and every reload used to
  // reopen it because nothing persisted the operator's choice.
  activityFeedOpen: false,
  // Collapsed by default — the burger in the top bar expands it on demand.
  railCollapsed: true,
  modals: {
    'persona-switcher': false,
    'studio-switcher': false,
    'self-book-generator': false,
    cancellation: false,
    'booking-modal': false,
    'rebooking-options': false,
    'deposit-link-modal': false,
    'rapport-note': false,
  },
  modalContext: {},
  notifications: [],
  incomingCalls: [],
  muted: false,
  narrationOn: false,
  workContext: null,
  setWorkContext: (ctx) => set({ workContext: ctx }),
  toggleControlPanel: () => set((s) => ({ controlPanelOpen: !s.controlPanelOpen })),
  toggleActivityFeed: () => set((s) => ({ activityFeedOpen: !s.activityFeedOpen })),
  toggleRail: () => set((s) => ({ railCollapsed: !s.railCollapsed })),
  setModal: (key, open, context) =>
    set((s) => ({
      modals: { ...s.modals, [key]: open },
      modalContext: open ? { ...s.modalContext, [key]: context ?? {} } : s.modalContext,
    })),
  pushNotification: (n) =>
    set((s) => ({
      notifications: [
        ...s.notifications,
        { ...n, id: nextId('toast'), createdAt: new Date().toISOString() },
      ],
    })),
  dismissNotification: (id) =>
    set((s) => ({ notifications: s.notifications.filter((n) => n.id !== id) })),
  addIncomingCall: (call) =>
    set((s) => ({ incomingCalls: [...s.incomingCalls, { ...call, status: 'ringing' }] })),
  setIncomingCallStatus: (id, status) =>
    set((s) => ({
      incomingCalls: s.incomingCalls.map((c) => (c.id === id ? { ...c, status } : c)),
    })),
  clearIncomingCalls: () => set({ incomingCalls: [] }),
  setMuted: (muted) => set({ muted }),
  setNarration: (on) => set({ narrationOn: on }),
    }),
    {
      name: 'ati-ui',
      storage: createJSONStorage(() => sessionStorage),
      // v1: the sidebar now defaults to collapsed. Drop any railCollapsed persisted
      // before this change so the new default reaches sessions that predate it,
      // without discarding the other saved chrome preferences.
      version: 1,
      migrate: (persisted, version) => {
        if (version < 1 && persisted && typeof persisted === 'object') {
          const { railCollapsed: _drop, ...rest } = persisted as Record<string, unknown>;
          return rest as unknown as UIState;
        }
        return persisted as UIState;
      },
      // Chrome preferences only. Modals, notifications and incoming calls are
      // live session state and must never be restored from a previous load.
      partialize: (s) => ({
        activityFeedOpen: s.activityFeedOpen,
        railCollapsed: s.railCollapsed,
      }),
    },
  ),
);
